import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppConfigEntities, AppConfigState, AppMode } from './appConfig.reducer';
import { Store } from '@ngrx/store';
import * as AppConfigSelectors from './appConfig.selectors';
import { AppCategory, ApplicationConfig, AppMappings, WebAppCategory, WebAppMapping } from './appConfig.models';
import * as AppConfigActions from './appConfig.actions';
import { Shifts } from '../../../../modules/battery-pool/models/models';
import { RatedCapacity } from '../../../../core/services';

@Injectable()
export class AppConfigStateService {
  constructor(private store: Store<AppConfigState>) {}

  currentApp(): Observable<ApplicationConfig> {
    return this.store.select(AppConfigSelectors.getCurrentAppConfig).pipe(filter(res => !!res));
  }

  isSmartBatterySelected(): Observable<boolean> {
    return this.store.select(AppConfigSelectors.isSmartBatterySelected);
  }

  isWatchlistCurrentApp(): Observable<boolean> {
    return this.store.select(AppConfigSelectors.isWatchlistCurrentSelected);
  }

  applications(): Observable<ApplicationConfig[]> {
    return this.store.select(AppConfigSelectors.getApplications).pipe(filter(item => item.length > 0));
  }

  appConfigEntities(): Observable<AppConfigEntities> {
    return this.store.select(AppConfigSelectors.getAppConfigEntites);
  }

  getAppByBaseLineId(baselineId: string): Observable<ApplicationConfig> {
    return this.store.select(AppConfigSelectors.getAppConfigByBaseLineId(baselineId));
  }

  appConfigState(): Observable<AppConfigState> {
    return this.store.select(AppConfigSelectors.getAppConfigState);
  }

  appsAndCurrentApp(): Observable<any> {
    return this.store.select(AppConfigSelectors.getAppsAndCurrentApp);
  }

  idsOrderedApplications(): Observable<ApplicationConfig[]> {
    return this.store.select(AppConfigSelectors.getIdsOrderedApplications).pipe(filter(item => item.length > 0));
  }

  dashboardApplications(): Observable<ApplicationConfig[]> {
    return this.store.select(AppConfigSelectors.getDashboardApplications).pipe(filter(item => item.length > 0));
  }

  isLoadedSuccessfully(): Observable<boolean> {
    return this.store
      .select(AppConfigSelectors.getIsLoadedSuccessfully)
      .pipe(filter(item => item === true || item === false));
  }

  getMapsLoaded(): Observable<boolean> {
    return this.store.select(AppConfigSelectors.getMapsLoaded);
  }

  setMapsLoaded(success: boolean) {
    return this.store.dispatch(new AppConfigActions.MapsLoadedAction(success));
  }

  getAppConfigMode(): Observable<AppMode> {
    return this.store.select(AppConfigSelectors.getAppConfigMode);
  }

  getAppPage(appMode: AppMode): Observable<string> {
    return this.store.select(AppConfigSelectors.lastPageAppMode).pipe(map(appModes => appModes[appMode]));
  }

  isHydrated(): Observable<boolean> {
    return this.store.select(AppConfigSelectors.getIsHydrated).pipe(filter(item => item === true || item === false));
  }

  setCurrentApp(application: ApplicationConfig, pageId, initialLoad: boolean = false, appMode: AppMode): void {
    if (application) {
      window.addTiming('AppConfigStateService:setCurrentApp' + application.id + ':' + pageId);
      this.store.dispatch(new AppConfigActions.ChangeAppConfig(application, pageId, false, initialLoad, appMode));
    }
  }

  fetchConfigs(): void {
    window.addTiming('AppConfigStateService:fetchConfigs');

    this.store.dispatch(new AppConfigActions.FetchConfigsAction());
  }

  fetchShifts(): void {
    this.store.dispatch(new AppConfigActions.FetchShift());
  }

  getShifts(): Observable<Shifts[]> {
    return this.store.select(AppConfigSelectors.getShifts);
  }

  fetchAppCategories(): void {
    this.store.dispatch(new AppConfigActions.FetchAppCategories());
  }

  fetchWebAppCategories(): void {
    this.store.dispatch(new AppConfigActions.FetchWebAppCategories());
  }

  getAppCategories(): Observable<AppCategory[]> {
    return this.store.select(AppConfigSelectors.getAppCategories);
  }

  getWebAppCategories(): Observable<WebAppCategory[]> {
    return this.store.select(AppConfigSelectors.getWebAppCategories);
  }

  deleteShift(id: number): void {
    this.store.dispatch(new AppConfigActions.DeleteShift(id));
  }

  addShift(shift: Shifts): void {
    this.store.dispatch(new AppConfigActions.AddShift(shift));
  }

  updateShift(shift: Shifts): void {
    this.store.dispatch(new AppConfigActions.UpdateShift(shift));
  }

  updateAppCategories(categories: AppCategory[], mappings: AppMappings[]): void {
    this.store.dispatch(new AppConfigActions.UpdateAppCategories(categories, mappings));
  }

  fetchAppMappings(): void {
    this.store.dispatch(new AppConfigActions.FetchAppMappings());
  }

  getAppMappings(): Observable<AppMappings[]> {
    return this.store.select(AppConfigSelectors.getAppMappings);
  }

  editAppMapping(mappings: AppMappings[]) {
    return this.store.dispatch(new AppConfigActions.EditAppMapping(mappings));
  }

  editAppCategory(categories: AppCategory[]) {
    return this.store.dispatch(new AppConfigActions.EditAppCategory(categories));
  }

  updateWebAppCategories(categories: WebAppCategory[], mappings: WebAppMapping[]): void {
    this.store.dispatch(new AppConfigActions.UpdateWebAppCategories(categories, mappings));
  }

  fetchWebAppMappings(): void {
    this.store.dispatch(new AppConfigActions.FetchWebAppMappings());
  }

  getWebAppMappings(): Observable<WebAppMapping[]> {
    return this.store.select(AppConfigSelectors.getWebAppMappings);
  }

  editWebAppMapping(mappings: WebAppMapping[]) {
    return this.store.dispatch(new AppConfigActions.EditWebAppMapping(mappings));
  }

  editWebAppCategory(categories: WebAppCategory[]) {
    return this.store.dispatch(new AppConfigActions.EditWebAppCategory(categories));
  }

  setRatedCapacity(rcap: RatedCapacity[]) {
    return this.store.dispatch(new AppConfigActions.UpdateRatedCapacity(rcap));
  }
}
